/**
 * NPC dialogue box — a pixelated panel at the bottom of the screen showing the NPC's head
 * portrait, name, a line of text, and optional reply buttons (for branching conversations).
 * Mounted lazily, toggled by the world controller.
 *
 * The text TYPES itself in (typewriter). While typing, a click (or E) reveals the whole line;
 * once revealed, reply buttons appear — or, for continue-nodes, a bouncing ▼ caret invites a
 * click/E to advance (`onContinue`). Reply labels prefixed with QUEST_OPT render a pixel "!"
 * quest marker. `advanceNpcDialog()` is the keyboard hook (E) for skip/advance.
 */
const TYPE_MS = 18; // per-character reveal cadence

let root: HTMLDivElement | null = null;
let headBox: HTMLDivElement | null = null;
let nameEl: HTMLDivElement | null = null;
let textEl: HTMLDivElement | null = null;
let optionsEl: HTMLDivElement | null = null;
let caretEl: HTMLDivElement | null = null;

// Live line state (typewriter + advance).
let fullText = "";
let typed = 0;
let typeTimer: number | null = null;
let pendingOptions: { label: string; quest: boolean }[] = [];
let onSelectCb: ((index: number) => void) | null = null;
let onContinueCb: (() => void) | null = null;

/** Prefix a reply label with this to render the pixel "!" quest marker before it. */
export const QUEST_OPT = "";

const STYLE_ID = "gl-npcdialog-style";
const CSS = `
@keyframes gl-npc-caret { 0%,100% { transform: translateY(0); } 50% { transform: translateY(4px); } }
.gl-npc-caret { animation: gl-npc-caret .8s ease-in-out infinite; }
.gl-npc-bang { display:inline-block; font-family:'Silkscreen', ui-monospace, monospace; font-weight:700;
  color:#ffd23f; text-shadow:1px 1px 0 #000; margin-right:7px; font-size:13px; }
/* CINEMATIC mode: the whole screen is the camera — every HUD overlay (they're all body>div
 * elements) hides except the game canvas (#game) and anything tagged .gl-cinema-keep. */
body.gl-cinema > div:not(.gl-cinema-keep):not(#game) { visibility: hidden !important; }
`;

function mount(): void {
  if (root) return;
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  root = document.createElement("div");
  root.style.cssText =
    "position:fixed;left:50%;bottom:120px;transform:translateX(-50%);z-index:12;display:none;" +
    "align-items:center;gap:16px;width:min(680px,90vw);padding:16px 20px;" +
    "background:var(--gl-panel);border:1px solid var(--gl-border);border-radius:10px;" +
    "box-shadow:0 18px 50px rgba(0,0,0,.7);font-family:var(--gl-font);color:var(--gl-text);";
  // clicks on the panel must not fall through to the world (camera/NPC click) underneath.
  root.addEventListener("pointerdown", (e) => e.stopPropagation());
  root.addEventListener("pointerup", (e) => e.stopPropagation());
  // Left-click on the panel: skip the typewriter, else advance a continue-node.
  root.addEventListener("click", () => advanceNpcDialog());

  headBox = document.createElement("div");
  headBox.style.cssText =
    "flex:0 0 auto;width:64px;height:64px;background:var(--gl-panel-2);border:1px solid var(--gl-border);border-radius:8px;" +
    "image-rendering:pixelated;display:flex;align-items:center;justify-content:center;overflow:hidden;";

  const right = document.createElement("div");
  right.style.cssText = "display:flex;flex-direction:column;gap:9px;min-width:0;flex:1;";
  nameEl = document.createElement("div");
  nameEl.style.cssText = "font-size:11px;letter-spacing:.18em;color:var(--gl-muted);text-transform:uppercase;";
  textEl = document.createElement("div");
  textEl.style.cssText = "font-size:13px;line-height:1.55;color:var(--gl-text);min-height:1.55em;";
  optionsEl = document.createElement("div");
  optionsEl.style.cssText = "display:flex;flex-direction:column;gap:6px;";
  right.append(nameEl, textEl, optionsEl);

  // The bouncing "click to continue" caret, pinned to the panel's bottom-right corner.
  caretEl = document.createElement("div");
  caretEl.className = "gl-npc-caret";
  caretEl.textContent = "▼";
  caretEl.style.cssText =
    "position:absolute;right:14px;bottom:8px;font-size:11px;color:#ffd23f;text-shadow:1px 1px 0 #000;" +
    "font-family:'Silkscreen', ui-monospace, monospace;display:none;pointer-events:none;";

  root.append(headBox, right, caretEl);
  document.body.appendChild(root);
}

function stopTyping(): void {
  if (typeTimer !== null) { clearInterval(typeTimer); typeTimer = null; }
}

/** After the line is fully revealed: show the reply buttons, or the continue caret. */
function finishLine(): void {
  stopTyping();
  typed = fullText.length;
  if (textEl) textEl.textContent = fullText;
  if (optionsEl) {
    optionsEl.replaceChildren();
    pendingOptions.forEach((opt, i) => {
      const btn = document.createElement("button");
      if (opt.quest) {
        const bang = document.createElement("span");
        bang.className = "gl-npc-bang";
        bang.textContent = "!";
        btn.append(bang, document.createTextNode(opt.label));
      } else {
        btn.textContent = `▸ ${opt.label}`;
      }
      btn.style.cssText =
        "text-align:left;font-family:var(--gl-font);font-size:12px;color:var(--gl-text);cursor:pointer;" +
        "background:var(--gl-panel-2);border:1px solid var(--gl-border);border-radius:7px;padding:8px 12px;transition:border-color .1s,background .1s;";
      if (opt.quest) btn.style.borderColor = "#5a4a26";
      btn.addEventListener("mouseenter", () => { btn.style.background = "#141414"; btn.style.borderColor = opt.quest ? "#ffd23f" : "#5a5a5a"; });
      btn.addEventListener("mouseleave", () => { btn.style.background = "var(--gl-panel-2)"; btn.style.borderColor = opt.quest ? "#5a4a26" : "var(--gl-border)"; });
      btn.addEventListener("click", (e) => { e.stopPropagation(); onSelectCb?.(i); });
      optionsEl!.appendChild(btn);
    });
  }
  // The caret shows when there's nothing to pick: a continue-node (▼ → next line) or the end (▼ → close).
  if (caretEl) caretEl.style.display = pendingOptions.length ? "none" : "block";
  if (root) root.style.cursor = pendingOptions.length ? "default" : "pointer";
}

/**
 * Show the dialogue. If `options` are given, render reply buttons; clicking one calls
 * `onSelect(index)`. With no options the panel is just a line — a click/E runs `onContinue`
 * (the next phase of the conversation) or, when absent, closes the dialog.
 */
export function showNpcDialog(
  head: HTMLCanvasElement | null,
  name: string,
  text: string,
  options?: string[],
  onSelect?: (index: number) => void,
  onContinue?: () => void,
): void {
  mount();
  if (headBox) {
    headBox.replaceChildren();
    if (head) {
      const c = document.createElement("canvas"); // copy (the source may also be in the target frame)
      c.width = head.width; c.height = head.height;
      c.getContext("2d")!.drawImage(head, 0, 0);
      c.style.cssText = "width:60px;height:60px;image-rendering:pixelated;";
      headBox.appendChild(c);
    }
  }
  if (nameEl) nameEl.textContent = name;
  // Reset the line + typewriter state.
  stopTyping();
  fullText = text;
  typed = 0;
  pendingOptions = (options ?? []).map((label) =>
    label.startsWith(QUEST_OPT) ? { label: label.slice(1), quest: true } : { label, quest: false });
  onSelectCb = onSelect ?? null;
  onContinueCb = onContinue ?? null;
  if (optionsEl) optionsEl.replaceChildren(); // replies only appear once the line finishes typing
  if (caretEl) caretEl.style.display = "none";
  if (textEl) textEl.textContent = "";
  if (root) { root.style.display = "flex"; root.style.position = "fixed"; }
  typeTimer = window.setInterval(() => {
    typed += 1;
    if (textEl) textEl.textContent = fullText.slice(0, typed);
    if (typed >= fullText.length) finishLine();
  }, TYPE_MS);
}

/**
 * The click/E advance: mid-typewriter it reveals the whole line; on a finished continue-node it
 * runs `onContinue` (or closes when the conversation is over). Returns true when the dialog
 * consumed the input (always, while open — replies must be clicked, not keyed past).
 */
export function advanceNpcDialog(): boolean {
  if (!isNpcDialogOpen()) return false;
  if (typeTimer !== null) { finishLine(); return true; } // still typing → skip to the full line
  if (pendingOptions.length) return true;                // replies showing → the click picks one
  if (onContinueCb) { const next = onContinueCb; onContinueCb = null; next(); return true; }
  hideNpcDialog();
  return true;
}

let cinematicOn = false;
let catcher: HTMLDivElement | null = null;

/** CINEMATIC presentation: hide every other HUD overlay, blow the panel up along the bottom of
 *  the screen, and let a click ANYWHERE advance (a full-screen catcher under the panel). The
 *  world controller flips this on for camera-tour catalogues (e.g. Sal's city intro). */
export function setNpcDialogCinematic(on: boolean): void {
  mount();
  if (on === cinematicOn) return;
  cinematicOn = on;
  root!.classList.add("gl-cinema-keep");
  document.body.classList.toggle("gl-cinema", on);
  if (on && !catcher) {
    catcher = document.createElement("div");
    catcher.className = "gl-cinema-keep";
    catcher.style.cssText = "position:fixed;inset:0;z-index:11;background:transparent;cursor:pointer;";
    catcher.addEventListener("click", () => advanceNpcDialog());
    document.body.appendChild(catcher);
  }
  if (catcher) catcher.style.display = on ? "block" : "none";
  // Big letterboxed caption bar vs the regular conversation panel.
  if (root) { root.style.width = on ? "min(1100px, 94vw)" : "min(680px, 90vw)"; root.style.bottom = on ? "30px" : "120px"; }
  if (textEl) { textEl.style.fontSize = on ? "19px" : "13px"; textEl.style.lineHeight = on ? "1.65" : "1.55"; }
  if (nameEl) nameEl.style.fontSize = on ? "13px" : "11px";
}
export function isNpcDialogCinematic(): boolean { return cinematicOn; }

export function hideNpcDialog(): void {
  stopTyping();
  onContinueCb = null;
  if (root) root.style.display = "none";
}

export function isNpcDialogOpen(): boolean {
  return !!root && root.style.display !== "none";
}

export function destroyNpcDialog(): void {
  stopTyping();
  setNpcDialogCinematic(false);
  catcher?.remove();
  catcher = null;
  root?.remove();
  root = headBox = nameEl = textEl = optionsEl = caretEl = null;
}
